import { Component, OnDestroy, OnInit } from '@angular/core';
import { filter, map, Observable, Subject, takeUntil, tap } from 'rxjs';
import { Application } from 'src/portaljs/constants/application';
import { PortalApp } from 'src/app/core/shared/portal-app';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { IframeService } from 'src/app/core/services/iframe.service';
import { IframeMetadata } from 'src/portaljs/models/iframeMetadata';
import { ConsoleLogger } from 'src/app/core/shared/console-logger';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
  selector: 'fss-portal-juniper-sidecar-overlay',
  templateUrl: './juniper-sidecar-overlay.component.html',
  styleUrls: ['./juniper-sidecar-overlay.component.scss'],
  standalone: false
})
export class JuniperSidecarOverlayComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  private readonly MIN_OVERLAY_PX = 76;
  public width = `${this.MIN_OVERLAY_PX}px`;
  public height = `${this.MIN_OVERLAY_PX}px`;
  public sidecarApp: PortalApp;
  private hasSidecarPermission = false;
  public readonly framableApps$: Observable<PortalApp[]> = this.iframeService.getOverlayFrames$()
    .pipe(
      filter((framableApps) => !!framableApps),
      map((framableApps: PortalApp[]) => framableApps.filter(app => app.isOverlay && app.app === Application.JUNIPER_SIDECAR)),
      tap(apps => this.sidecarApp = apps.length > 0 ? apps[0] : null),
      takeUntil(this.ngUnsubscribe)
    ); // DO NOT change this array reference.


  constructor(private portalStateService: PortalStateService, private iframeService: IframeService, private authService: AuthService) {
  }


  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }


  public ngOnInit(): void {
    this.iframeService.getMetadataMessage$()
      .pipe(
        filter((iframeMetadata) => !!iframeMetadata),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((iframeMetadata) => {
        this.updateSize(iframeMetadata[Application.JUNIPER_SIDECAR]);
      });

    this.authService.getGrants$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((grants) => this.hasSidecarPermission = this.iframeService.isJuniperSidecarEnabled(grants));
  }

  public isVisible(): boolean {
    let isVisible = false;
    if (this.hasSidecarPermission) {
      if (!this.sidecarApp?.loadingError && this.iframeService.isReadyResponseReceived(this.sidecarApp)) {
        const activeApp = this.portalStateService.getActiveApp();
        isVisible = (activeApp && !activeApp.loadingError && this.iframeService.isReadyResponseReceived(activeApp)
                    && (activeApp.app === Application.DESTINY || activeApp.app === Application.DASHBOARD || activeApp.app === Application.DESTINY_UI));
      }
    }
    return isVisible;
  }


  public updateSize(iframeMetadata: IframeMetadata): void {
    this.width = this.calculateOverlaySize(iframeMetadata, 'width');
    this.height = this.calculateOverlaySize(iframeMetadata, 'height');
    // Update PortalStateService's sidecarMinimized - used in user session maintenance.
    const isMinimized = this.width === `${this.MIN_OVERLAY_PX}px`
      && this.height === `${this.MIN_OVERLAY_PX}px`;
    this.portalStateService.setSidecarMinimized(isMinimized);
  }


  private calculateOverlaySize(iframeMetadata: IframeMetadata, dim: 'width' | 'height'): string {
    let calcSize = `${this.MIN_OVERLAY_PX}px`;
    if (iframeMetadata) {
      const isPx = iframeMetadata[dim]?.includes('px');
      const isPct = iframeMetadata[dim]?.includes('%');
      if (isPx || isPct) {
        const metadataSize = iframeMetadata[dim]?.replace(/(px|%)/g, '');
        if (metadataSize && !isNaN(Number(metadataSize)) && Number(metadataSize) > 0) {
          if (isPx) {
            if (Number(metadataSize) >= this.MIN_OVERLAY_PX) {
              calcSize = iframeMetadata[dim];
            }
          } else if (isPct) {
            const framesDiv = document.getElementById('frames-div');
            if (framesDiv) {
              const frameSize = (dim === 'width') ? framesDiv.offsetWidth : framesDiv.offsetHeight;
              const sizeInPx = frameSize * (Number(metadataSize) / 100);
              if (sizeInPx >= this.MIN_OVERLAY_PX) {
                calcSize = iframeMetadata[dim];
              }
            }
          }
        }
      }
    }
    return calcSize;
  }

  public onError(overlayApp: PortalApp): void {
    ConsoleLogger.error('Failed to load Juniper Sidecar: ' + overlayApp.url);
    overlayApp.loadingError = true;
    overlayApp.setReadyResponseReceived(true);
  }
}
